import { ButtonVariants } from 'src/api/models/Button';
import { palette } from '../colors';

interface ButtonStyles {
  textColor: string;
  backgroundColor: string;
}

export type ColorScheme =
  | 'Standard'
  | 'Pro'
  | 'Violet'
  | 'ProYellow'
  | 'SoftBlue'
  | 'SeaTurquoise'
  | 'Magenta'
  | 'Black'
  | 'SnowFlower'
  | 'White'
  | 'Aqua'
  | 'NeonGreen'
  | 'AccentBlue'
  | 'Amber'
  | 'LightTeal'
  | 'StandardYellow'
  | 'Sunshine'
  | 'Wheat'
  | 'Sunglow';

export default function getButtonStyleByColorScheme(scheme: ColorScheme, variant: ButtonVariants): ButtonStyles {
  const schemeHashMap: { [key: string]: { [key: string]: ButtonStyles } } = {
    Standard: {
      primary: {
        textColor: palette.white,
        backgroundColor: palette.deepPurple,
      },
      secondary: {
        textColor: palette.deepPurple,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.snowFlower,
        backgroundColor: 'transparent',
      }, // Keep snowFlower while we don't have a new color for text links
    },
    SnowFlower: {
      primary: {
        textColor: palette.white,
        backgroundColor: palette.snowFlower,
      },
      secondary: {
        textColor: palette.snowFlower,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.snowFlower,
        backgroundColor: 'transparent',
      },
    },
    Pro: {
      primary: { textColor: palette.white, backgroundColor: palette.proBlue },
      secondary: { textColor: palette.proBlue, backgroundColor: palette.white },
      tertiary: { textColor: palette.proBlue, backgroundColor: 'transparent' },
    },
    Violet: {
      primary: {
        textColor: palette.white,
        backgroundColor: palette.deepPurple,
      },
      secondary: {
        textColor: palette.deepPurple,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.deepPurple,
        backgroundColor: 'transparent',
      },
    },
    ProYellow: {
      primary: { textColor: palette.text, backgroundColor: palette.proYellow },
      secondary: {
        textColor: palette.proYellow,
        backgroundColor: palette.text,
      },
      tertiary: {
        textColor: palette.proYellow,
        backgroundColor: 'transparent',
      },
    },
    SoftBlue: {
      primary: { textColor: palette.black, backgroundColor: palette.softBlue },
      secondary: {
        textColor: palette.softBlue,
        backgroundColor: palette.black,
      },
      tertiary: { textColor: palette.softBlue, backgroundColor: 'transparent' },
    },
    SeaTurquoise: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.seaTurquoise,
      },
      secondary: {
        textColor: palette.seaTurquoise,
        backgroundColor: palette.greyText,
      },
      tertiary: {
        textColor: palette.seaTurquoise,
        backgroundColor: 'transparent',
      },
    },
    Magenta: {
      primary: { textColor: palette.white, backgroundColor: palette.magenta },
      secondary: { textColor: palette.magenta, backgroundColor: palette.white },
      tertiary: { textColor: palette.magenta, backgroundColor: 'transparent' },
    },
    Black: {
      primary: { textColor: palette.white, backgroundColor: palette.black },
      secondary: {
        textColor: palette.greyText,
        backgroundColor: palette.white,
      },
      tertiary: { textColor: palette.greyText, backgroundColor: 'transparent' },
    },
    White: {
      primary: { textColor: palette.white, backgroundColor: 'transparent' },
      secondary: { textColor: palette.white, backgroundColor: 'transparent' },
      tertiary: { textColor: palette.white, backgroundColor: 'transparent' },
    },
    Aqua: {
      primary: { textColor: palette.greyText, backgroundColor: palette.aqua },
      secondary: { textColor: palette.greyText, backgroundColor: palette.aqua },
      tertiary: { textColor: palette.greyText, backgroundColor: palette.aqua },
    },
    NeonGreen: {
      primary: {
        textColor: palette.text,
        backgroundColor: palette.brandedLightGreen,
      },
      secondary: {
        textColor: palette.brandedLightGreen,
        backgroundColor: palette.text,
      },
      tertiary: {
        textColor: palette.brandedLightGreen,
        backgroundColor: 'transparent',
      },
    },
    StandardYellow: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.standardYellow,
      },
      secondary: {
        textColor: palette.standardYellow,
        backgroundColor: 'transparent',
      },
      tertiary: {
        textColor: palette.standardYellow,
        backgroundColor: 'transparent',
      },
    },
    AccentBlue: {
      primary: {
        textColor: palette.white,
        backgroundColor: palette.accentBlue,
      },
      secondary: {
        textColor: palette.accentBlue,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.accentBlue,
        backgroundColor: 'transparent',
      },
    },
    Amber: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.amber,
      },
      secondary: {
        textColor: palette.amber,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.amber,
        backgroundColor: 'transparent',
      },
    },
    LightTeal: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.lightTeal,
      },
      secondary: {
        textColor: palette.lightTeal,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.lightTeal,
        backgroundColor: 'transparent',
      },
    },
    Sunshine: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.sunshine,
      },
      secondary: {
        textColor: palette.sunshine,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.sunshine,
        backgroundColor: 'transparent',
      },
    },
    Wheat: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.lightWheat,
      },
      secondary: {
        textColor: palette.lightWheat,
        backgroundColor: palette.greyText,
      },
      tertiary: {
        textColor: palette.lightWheat,
        backgroundColor: 'transparent',
      },
    },
    Sunglow: {
      primary: {
        textColor: palette.greyText,
        backgroundColor: palette.sunglow,
      },
      secondary: {
        textColor: palette.sunglow,
        backgroundColor: palette.white,
      },
      tertiary: {
        textColor: palette.sunglow,
        backgroundColor: 'transparent',
      },
    },
  };

  return schemeHashMap[scheme ?? 'Standard'][variant ?? 'primary'];
}
